import React, { useState } from 'react';
import { View, TouchableOpacity, Image } from 'react-native';
import MyAppText from '../MyAppText';
import { translate } from '../../services/translate';
import { faCalendarCheck, faLocationDot, faTimes, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import PlateImage from '../lpr/PlateImage';
import { WindowInformation } from '../../services/window-information';
import OccurrenceVideoModal from '../dispatch/occurrenceForm/OccurrenceVideoModal';
import { appAgentService } from '../../services/central-api/app-agent';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { InfoInRow } from '../InfoInRow';
import Card from '../Card';
import { RootNavigation } from '../../Routes';

export function PlateDetectedOccurrence({ occurrence, event, navigation, imageUrl }: {
    occurrence: Occurrence;
    event: LprDetection | CitizenLprDetection;
    navigation: RootNavigation;
    imageUrl: string;
}) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const [isModalVisible, setModalVisible] = useState(false);

    if (!event) {
        return <></>;
    }

    function getOccurrenceTitle() {
        if (occurrence.triggerType == 'clone_suspect') {
            return translate('cloneSuspectAlert');
        } else if (occurrence.triggerType == 'irregular_vehicle') {
            return translate('irregularVehicleAlert');
        } else if (occurrence.triggerType == 'plate_detected_by_mobile_agent') {
            return translate('plateDetectedByMobileAgentAlert');
        } else if (occurrence.triggerType == 'illegal_debris') {
            return translate('illegalDebrisAlert');
        } else if (occurrence.triggerType == 'plate_detected_by_citizen_app') {
            return translate('plateDetectedByCitizenAppAlert');
        }
        return translate('plateDetectedAlert');
    }

    return (
        <View style={{ gap: 20 }}>
            <View style={{ gap: 5 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <TouchableOpacity
                            onPress={() => {
                                window.open(`/dispatch/occurrences/detail/${occurrence?.id}`);
                            }}
                            disabled={windowInfo.isMobile}
                        >
                            <MyAppText style={{ fontSize: 16, color: theme.colors.textColor, fontWeight: 'bold' }}>
                                {translate('nProtocol')}: {occurrence?.id}
                            </MyAppText>
                        </TouchableOpacity>
                        <MyAppText> </MyAppText>
                        <MyAppText style={{ fontSize: 14, color: theme.colors.textColor }}>
                            ({translate('team')} {translate(occurrence.OccurrenceUnits?.length && occurrence.OccurrenceUnits[0].OccurrenceUnit.isStarter ? 'starter' : 'support').toLowerCase()})
                        </MyAppText>
                    </View>
                    <TouchableOpacity onPress={() => navigation.navigate('AppAgent', { screen: 'AgentOccurrences' })}>
                        <FontAwesomeIcon color={theme.colors.iconColor} fontSize={30} icon={faTimes} />
                    </TouchableOpacity>
                </View>
                <MyAppText style={styles.alertText}>
                    {getOccurrenceTitle()}
                </MyAppText>
                <MyAppText style={styles.alertText}>{event.plate}</MyAppText>
                <View style={{ alignItems: 'center' }}>
                    {event.platePosition ?
                        <PlateImage src={imageUrl} platePosition={event.platePosition} width={300} height={150} />
                        : <Image
                            source={{ uri: occurrence.details?.imageUrl }}
                            style={{ width: 300, height: 150 }}
                        />
                    }
                </View>
            </View>
            <Card>
                <View style={{ gap: 15 }}>
                    {occurrence.triggerType !== 'plate_detected_by_mobile_agent' && occurrence.triggerType !== 'plate_detected_by_citizen_app' &&
                        <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faPlay as IconDefinition} style={{ width: 22 }} color={theme.colors.iconColor} />
                            <TouchableOpacity onPress={() => setModalVisible(true)}>
                                <MyAppText style={styles.watchVideoText}>
                                    {translate('watchVideo')}
                                </MyAppText>
                            </TouchableOpacity>
                        </View>
                    }
                    <View style={{ gap: 5 }}>
                        <View style={{ rowGap: 5, flexDirection: 'column' }}>
                            <InfoInRow value={event['vehicleDescription.brand']} title='brand' />
                            <InfoInRow value={event['vehicleDescription.model']} title='model' />
                            <InfoInRow value={event['vehicleDescription.color']} title='color' />
                            <InfoInRow value={event['vehicleDescription.type']} title='vehicleType' />
                            <InfoInRow value={event['vehicleDescription.city']} title='origin' />
                            <InfoInRow value={event['vehicleDescription.modelYear']} title='year' />
                        </View>
                    </View>
                    <View style={{ gap: 3 }}>
                        <MyAppText style={styles.text}>{translate('location')}</MyAppText>
                        {'camera' in event && <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faLocationDot} style={{ width: 14 }} color={theme.colors.iconColor} />
                            <MyAppText style={styles.detectionInfoText}>
                                {event.camera?.title}
                            </MyAppText>
                        </View>}
                        <View style={styles.visualizedRow} >
                            <FontAwesomeIcon icon={faCalendarCheck} style={{ width: 14 }} color={theme.colors.iconColor} />
                            <MyAppText style={styles.detectionInfoText}>
                                {moment(new Date(event.time)).format('DD/MM/YYYY HH:mm:ss')}
                            </MyAppText>
                        </View>
                    </View>
                </View>
                <View>
                    <View style={styles.visualizedRow}>
                        <MyAppText style={styles.cardBoldText}>{translate('occurrenceId')}:</MyAppText>
                        <MyAppText style={styles.cardText}>{occurrence.id}</MyAppText>
                    </View>
                    <View style={styles.visualizedRow}>
                        <MyAppText style={styles.cardBoldText}>{translate('priority')}:</MyAppText>
                        <MyAppText style={[styles.cardText, occurrence.priority === 'maximum' ? { color: '#FF0027' } : undefined]}>{occurrence.priority ? translate(occurrence.priority) : ''}</MyAppText>
                    </View>
                    <View>
                        <View style={{ paddingBottom: 30 }}>
                            <MyAppText style={styles.cardBoldText}>{translate('narrative')}:</MyAppText>
                            <MyAppText style={styles.cardText}>{occurrence.narrative}</MyAppText>
                        </View>
                    </View>
                </View>
            </Card>
            {occurrence.id && <OccurrenceVideoModal
                occurrenceId={occurrence.id}
                isModalVisible={isModalVisible}
                setModalVisible={setModalVisible}
                occurrenceTime={new Date(event.time).getTime()}
                apiService={appAgentService}
            />}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    visualizedRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5
    },
    detectionInfoText: {
        color: theme.colors.textColor,
        fontSize: 12
    },
    text: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 16
    },
    alertText: {
        color: theme.colors.textColor,
        fontSize: 16,
        fontWeight: 'bold',
    },
    cardText: {
        color: theme.colors.textColor,
        fontSize: 12
    },
    cardBoldText: {
        color: theme.colors.textColor,
        fontWeight: 'bold',
        fontSize: 14
    },
    watchVideoText: {
        color: theme.colors.textColor,
        fontSize: 16,
        textDecorationLine: 'underline'
    }
}));
